<template>
  <ai-hotel-card
    :brand="brand"
    :city="city"
    :country="country"
    :hotel-id="hotelId"
    :hotel-name="hotelName"
    :image="image"
    :images="images"
    :pictograms="pictograms"
    :rating="rating"
    :query="query"
    :reviews="reviews"
    :slug="slug">
    <template #aside>
      <div
        class="SearchHotelCard-asideColumn"
        :class="{ 'SearchHotelCard-asideColumn--price': hasOffer }">
        <div class="SearchHotelCard-allInclusive">
          <all-inclusive-tag :inclusiveness="inclusiveness" />
        </div>

        <div>
          <ai-typo
            v-if="hasOffer"
            as="p"
            variant="paragraph-04-bold"
            class="SearchHotelCard-nightsAndGuests">
            {{ $t('booking.roomRate.night', { count: nights || 1 }) }}
            |
            {{ $t('booking.roomRate.guest', { count: guests }) }}
          </ai-typo>
          <ai-typo
            as="p"
            variant="overline-01"
            color="stratos-800"
            class="SearchHotelCard-ratesFrom">
            {{ $t('search.hotelCard.ratesFrom') }}
          </ai-typo>
          <template v-if="searchWithBestOffers && hasOffer">
            <aside-prices
              class="SearchHotelCard-asidePrice"
              :is-member="userStore.isLogged"
              :public-price="publicPrice || 0"
              :member-price="memberPrice || 0"
              :tax-label="taxLabel"
              :currency="props.currency"
              :user-currency="targetCurrency"
              data-testid="search.hotel.prices" />
            <template v-if="!isFlightHotelAvailable">
              <ai-button
                class="SearchHotelCard-button"
                variant="primary"
                :loading="loading"
                :label="$t('search.hotelCard.bookingCtaLabel')"
                :aria-label="
                  $t('search.hotelCard.bookingCtaLabel', { hotelName })
                "
                data-testid="search.hotel.selectRoom"
                @click="bookingAction" />
            </template>
            <template v-else>
              <template v-if="includeFlights">
                <ai-button
                  class="SearchHotelCard-button"
                  variant="primary"
                  :loading="loading"
                  :label="$t('search.hotelCard.searchFlightCtaLabel')"
                  :aria-label="
                    $t('search.hotelCard.searchFlightCtaLabel', { hotelName })
                  "
                  @click="searchFlightAction" />
              </template>
              <template v-else>
                <ai-button
                  class="SearchHotelCard-button"
                  variant="primary"
                  :loading="loading"
                  :label="$t('search.hotelCard.roomOnlyCtaLabel')"
                  :aria-label="
                    $t('search.hotelCard.roomOnlyCtaLabel', { hotelName })
                  "
                  @click="bookingAction" />
                <ai-button
                  class="SearchHotelCard-flightButton"
                  variant="secondary"
                  :loading="loading"
                  :label="$t('search.hotelCard.flightCtaLabel')"
                  :aria-label="
                    $t('search.hotelCard.flightCtaLabel', { hotelName })
                  "
                  @click="flightAction" />
              </template>
            </template>
          </template>
          <template v-else-if="searchWithBestOffers && !hasOffer">
            <div class="SearchHotelCard-noAvailabilityWarning">
              <ai-icon name="warning" />
              <ai-typo as="span" variant="paragraph-04-bold" color="red-600">
                {{ $t('search.hotelCard.noAvailabilityWarning') }}
              </ai-typo>
            </div>
            <ai-link
              icon-left="calendar"
              :text="$t('search.hotelCard.modifyDatesCtaLabel')"
              @click="$emit('openDatepicker')" />
          </template>
          <template v-else>
            <ai-button
              class="SearchHotelCard-datesButton"
              variant="tertiary"
              @click="$emit('openDatepicker')">
              <span>
                <ai-icon
                  class="SearchHotelCard-datesIcon"
                  :size="24"
                  name="calendar"
                  fill="stratos-900" />
              </span>
              <ai-typo
                class="SearchHotelCard-datesSpan"
                variant="button-bold-underline"
                color="stratos-900"
                as="span">
                {{ $t('search.hotelCard.datesCtaLabel') }}
              </ai-typo>
            </ai-button>
          </template>
        </div>
      </div>
    </template>
  </ai-hotel-card>
</template>

<script setup lang="ts">
import { getAllInclusivePlan } from '~/domains/booking/helpers/isAllInclusive.helper';
import { useFlightHotelAvailability } from '~/domains/feature';
import type { AiPicture, Hotel, SearchHotel } from '~/domains/graphql';
import { useUserStore } from '~/domains/user';

export type Pictogram = {
  label: string;
  url: string;
};

export type BookingActionParams = {
  hotelId: string;
  country: string;
  countryCode: string | null;
  city: string;
  zipCode: string | null;
};

export type FlightActionParams = {
  hotelSlug: string;
};

export type SearchFlightActionParams = {
  hotelId: string;
  hotelName: string;
  destinationId: string;
};

type Props = {
  adults?: number;
  brand?: string;
  childrenAges?: number[];
  city: string;
  country: string;
  countryCode: string | null;
  currency?: string | null;
  dateIn?: string;
  destinationId?: string;
  hotel?: SearchHotel;
  hotelId: string;
  hotelName: string;
  image?: AiPicture | null;
  images?: AiPicture[] | null;
  includeFlights?: boolean;
  loading: boolean;
  memberMealPlan?: string;
  memberPrice?: number | null;
  nights?: number;
  pictograms?: Pictogram[];
  publicMealPlan?: string;
  publicPrice?: number | null;
  rating?: number | null;
  reviews?: number | null;
  slug: string;
  targetCurrency?: string;
  zipCode: string | null;
  bestOffer?: any;
};

const props = withDefaults(defineProps<Props>(), {
  adults: 0,
  brand: undefined,
  childrenAges: undefined,
  currency: undefined,
  dateIn: undefined,
  destinationId: '',
  hotel: undefined,
  includeFlights: false,
  image: undefined,
  images: null,
  memberMealPlan: undefined,
  memberPrice: 0,
  nights: undefined,
  pictograms: undefined,
  publicMealPlan: undefined,
  publicPrice: 0,
  rating: 0,
  reviews: 0,
  targetCurrency: undefined,
  userCurrency: undefined,
  bestOffer: {},
});

interface Emits {
  (event: 'bookingAction', value: BookingActionParams): void;
  (event: 'flightAction', value: FlightActionParams): void;
  (event: 'openDatepicker'): void;
  (event: 'searchFlightAction', value: SearchFlightActionParams): void;
}

const emit = defineEmits<Emits>();

const userStore = useUserStore();
const { isFlightHotelAvailable } = useFlightHotelAvailability();

const { t } = useI18n();
const route = useRoute();
const areAllTaxesIncluded = useAreAllTaxesIncluded();

const hotel = computed(() => props.hotel as Hotel | undefined);

const inclusiveness = computed(() =>
  getAllInclusivePlan({
    type: 'hotel',
    hotel: hotel.value,
    isLogged: userStore.isLogged,
    forcedMealPlan:
      userStore.isLogged && props.memberMealPlan
        ? props.memberMealPlan
        : props.publicMealPlan,
  }),
);

const guests = computed(() => {
  const childrenCount = Array.isArray(props.childrenAges)
    ? props.childrenAges.length
    : 0;

  return props.adults + childrenCount;
});

const searchWithBestOffers = computed(() => {
  return Boolean(props.nights && props.adults);
});

const hasOffer = computed(() => {
  return Boolean(props.memberPrice || props.publicPrice);
});

const bookingAction = () => {
  emit('bookingAction', {
    hotelId: props.hotelId,
    country: props.country,
    countryCode: props.countryCode,
    city: props.city,
    zipCode: props.zipCode,
  });
};

const searchFlightAction = () => {
  emit('searchFlightAction', {
    hotelId: props.hotelId,
    hotelName: props.hotelName,
    destinationId: props.destinationId,
  });
};

const flightAction = () => {
  emit('flightAction', {
    hotelSlug: props.slug,
  });
};

const feesAndTaxes = computed<number>(
  () =>
    (props?.bestOffer?.publicOfferPricing?.afterTax ?? 0) -
    (props?.bestOffer?.publicOfferPricing?.hotelKeeper ?? 0),
);

const convertedFeesAndTaxes = useConvertToUserCurrency(
  feesAndTaxes,
  props.currency,
  props.targetCurrency,
);

const taxLabel = computed<string>(() =>
  feesAndTaxes.value === 0 || areAllTaxesIncluded?.value
    ? t('booking.roomRate.freeTaxLabel')
    : t('booking.roomRate.taxLabel', {
        currency: convertedFeesAndTaxes.value.currency?.code,
        feesAndTaxes: convertedFeesAndTaxes.value.amount.toFixed(2),
      }),
);

const query = reactive<{ dates?: string; roomsAndGuests?: string }>({});

watch(
  () => route.query,
  newQuery => {
    if (newQuery.dates) {
      query.dates = newQuery.dates as string;
    } else {
      delete query.dates;
    }

    if (newQuery.roomsAndGuests) {
      query.roomsAndGuests = newQuery.roomsAndGuests as string;
    } else {
      delete query.roomsAndGuests;
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/mq';

.SearchHotelCard-asideColumn {
  display: flex;
  flex-direction: column;

  white-space: break-spaces;

  @media (mq.$from-large) {
    gap: constants.$inner-02;
    justify-content: space-between;
    align-items: flex-end;
    text-align: end;
  }

  &--price {
    gap: 0;
  }
}

.SearchHotelCard-allInclusive {
  display: flex;
  align-self: flex-start;
  justify-content: flex-end;
  margin-bottom: constants.$margin-01;

  @media (mq.$from-large) {
    width: 100%;
  }
}

.SearchHotelCard-memberChips {
  margin-bottom: constants.$margin-01;
  @media (mq.$from-large) {
    @include mixins.rem-fallback(margin-bottom, 55);
  }
}

.SearchHotelCard-nightsAndGuests {
  margin-bottom: constants.$inner-03;
}

.SearchHotelCard-ratesFrom {
  margin-bottom: constants.$inner-01;

  @media (mq.$from-large) {
    margin-bottom: constants.$inner-01;
  }
}

.SearchHotelCard-button {
  @media (mq.$from-large) {
    @include mixins.rem-fallback(width, 278);
  }
}

.SearchHotelCard-flightButton {
  margin-top: constants.$margin-01;
  @media (mq.$from-large) {
    @include mixins.rem-fallback(width, 278);
  }
}

.SearchHotelCard-noAvailabilityWarning {
  display: flex;
  align-items: center;
  gap: constants.$inner-01;
  color: colors.$red-600;
}

.SearchHotelCard-datesButton {
  @media (mq.$from-large) {
    margin-left: constants.$margin-05;
  }
}

.SearchHotelCard-datesSpan {
  text-align: right;
}

.SearchHotelCard-datesIcon {
  display: flex;
  margin-right: constants.$inner-02;
}

.SearchHotelCard-asidePrice {
  margin-bottom: constants.$margin-01;
}
</style>
